/**
 * หาว่าตัวไหน "ยังไม่แน่น" — ใช้ร่วมกันทั้ง drill (kana) และ SRS (bushu/kanji)
 *
 * โจทย์: เก่งกับไม่เก่งไม่ได้แยกกันที่ถูก/ผิดอย่างเดียว
 * ตอบถูกแต่ต้องนึกนาน = ยังไม่ใช่ความจำอัตโนมัติ ซึ่งเป็นเป้าหมายจริงของ kana
 * (อ่านมังงะแล้วต้องหยุดคิดทุกตัว = อ่านไม่ออกในทางปฏิบัติ)
 */
import type { Card, CardId, DrillStat, Progress, ReviewLog } from './types'
import { CardState } from './types'
import { LEECH_THRESHOLD } from './srs'

export type WeakReason =
  | 'ok' // แน่นแล้ว
  | 'new' // ยังไม่เคยเจอ — ไม่ใช่จุดอ่อน แค่ยังไม่ถึงคิว
  | 'wrong' // ผิดบ่อย
  | 'recent' // เพิ่งผิดครั้งล่าสุด
  | 'slow' // ถูกแต่ช้ากว่าตัวอื่นมาก
  | 'lapsed' // เคยจำได้แล้วลืม (FSRS lapse)
  | 'leech' // ผิดซ้ำจนต้องเปลี่ยนวิธีจำ

export interface Weakness {
  cardId: CardId
  /** 0 = แน่นแล้ว · ยิ่งมากยิ่งอ่อน — ใช้เรียงลำดับความเร่งด่วน */
  score: number
  reason: WeakReason
  seen: number
  wrong: number
  /** 0–1 · -1 = ยังไม่เคยเจอ */
  accuracy: number
  avgMs: number
}

/** ช้ากว่าค่ากลางของ deck กี่เท่าถึงนับว่ายังไม่คล่อง */
const SLOW_FACTOR = 1.8
/** ต่ำกว่านี้ถือว่ายังไม่แม่น */
const ACCURACY_FLOOR = 0.85
/** ผิดครั้งล่าสุดภายในกี่วันถึงยังนับว่าสด */
const RECENT_DAYS = 3

function median(nums: number[]): number {
  if (nums.length === 0) return 0
  const s = [...nums].sort((a, b) => a - b)
  const mid = Math.floor(s.length / 2)
  return s.length % 2 === 0 ? ((s[mid - 1] ?? 0) + (s[mid] ?? 0)) / 2 : (s[mid] ?? 0)
}

/** เวลาตอบกลางของ deck — เกณฑ์ "ช้า" ต้องเทียบกับตัวเอง ไม่ใช่ตัวเลขตายตัว */
export function medianMs(stats: DrillStat[]): number {
  return median(stats.filter((s) => s.avgMs > 0).map((s) => s.avgMs))
}

export function drillWeakness(
  card: Card,
  stat: DrillStat | undefined,
  refMs: number,
  now = Date.now(),
): Weakness {
  const base = { cardId: card.id, seen: stat?.seen ?? 0, wrong: stat?.wrong ?? 0 }

  if (!stat || stat.seen === 0) {
    return { ...base, score: 0, reason: 'new', accuracy: -1, avgMs: 0 }
  }

  const accuracy = (stat.seen - stat.wrong) / stat.seen
  const recent = stat.lastWrongAt > 0 && now - stat.lastWrongAt < RECENT_DAYS * 86_400_000
  const slow = refMs > 0 && stat.avgMs > refMs * SLOW_FACTOR

  let score = 0
  let reason: WeakReason = 'ok'

  if (accuracy < ACCURACY_FLOOR) {
    // ยิ่งพลาดสัดส่วนมากยิ่งด่วน · คูณจำนวนครั้งที่ผิดเพื่อให้ตัวที่ผิดซ้ำเด่นกว่าตัวที่ผิดครั้งเดียว
    score = (1 - accuracy) * 100 + stat.wrong * 5
    reason = 'wrong'
  } else if (recent) {
    score = 40
    reason = 'recent'
  } else if (slow) {
    score = Math.min(35, (stat.avgMs / refMs - 1) * 25)
    reason = 'slow'
  }

  // ถูกติดกันหลายครั้งแล้วก็ผ่อนความด่วนลง
  score = Math.max(0, score - stat.streak * 4)
  if (score === 0) reason = 'ok'

  return { ...base, score, reason, accuracy, avgMs: stat.avgMs }
}

/**
 * @param refMs เวลาตอบกลางของ deck · 0 = ยังไม่มีข้อมูลพอ ข้ามสัญญาณ "ช้า" ไป
 */
export function srsWeakness(card: Card, p: Progress | undefined, refMs = 0): Weakness {
  const base = { cardId: card.id, seen: p?.reps ?? 0, wrong: p?.lapses ?? 0 }

  if (!p || p.state === CardState.New) {
    return { ...base, score: 0, reason: 'new', accuracy: -1, avgMs: 0 }
  }

  const accuracy = p.reps > 0 ? Math.max(0, (p.reps - p.lapses) / p.reps) : -1
  const avgMs = p.avgMs ?? 0

  if (p.lapses >= LEECH_THRESHOLD) {
    return { ...base, score: 100 + p.lapses, reason: 'leech', accuracy, avgMs }
  }
  if (p.state === CardState.Relearning) {
    return { ...base, score: 60 + p.lapses * 5, reason: 'lapsed', accuracy, avgMs }
  }
  if (p.lapses > 0) {
    // difficulty ของ FSRS อยู่ 1–10 · สูง = ต้องทวนถี่กว่าจะจำได้
    return { ...base, score: p.lapses * 12 + p.difficulty, reason: 'wrong', accuracy, avgMs }
  }
  /*
   * ไม่เคยผิดเลยแต่ยังต้องนึกนาน — จำได้แบบต้องคิด ยังไม่ใช่ความจำอัตโนมัติ
   * ฝั่ง drill ใช้สัญญาณนี้มาตั้งแต่แรก ส่วนฝั่ง SRS เพิ่งมีข้อมูลเวลาให้ใช้
   */
  if (refMs > 0 && avgMs > refMs * SLOW_FACTOR) {
    return { ...base, score: Math.min(35, (avgMs / refMs - 1) * 25), reason: 'slow', accuracy, avgMs }
  }
  return { ...base, score: 0, reason: 'ok', accuracy, avgMs }
}

/** เวลาตอบกลางของ deck แบบ SRS — เกณฑ์ "ช้า" ต้องเทียบกับตัวเอง */
export function medianProgressMs(rows: Progress[]): number {
  return median(rows.map((p) => p.avgMs ?? 0).filter((n) => n > 0))
}

// ---------- ความอ่อนรายโหมด ----------

/**
 * จำความหมายได้ กับ นึกรูปออก เป็นคนละทักษะ
 *
 * เห็น 好 แล้วบอกว่า "ชอบ" ทำได้ ไม่ได้แปลว่าเห็นคำว่า "ชอบ" แล้วนึกรูป 好 ออก
 * — ทางกลับยากกว่าเสมอ · คะแนนความอ่อนก้อนเดียวกลบเรื่องนี้ทั้งหมด แล้วบอกว่า
 * ใบนี้แน่นแล้วทั้งที่แน่นแค่ทางเดียว
 *
 * ไม่ต้องเพิ่ม store ใหม่: ReviewLog บันทึก mode ไว้ทุกครั้งอยู่แล้ว
 */
export interface ModeStat {
  seen: number
  wrong: number
  avgMs: number
  lastWrongAt: number
}

/** ต่ำกว่านี้ยังตัดสินไม่ได้ ใช้สถิติรวมไปก่อน */
export const MODE_MIN_SEEN = 3

/** สรุป log เป็นสถิติต่อ (ใบ, โหมด) — key คือ `cardId|mode` */
export function statsByMode(logs: readonly ReviewLog[]): Map<string, ModeStat> {
  const out = new Map<string, ModeStat>()
  for (const l of logs) {
    // โหมดเรียนไม่ใช่การทดสอบ ไม่มีถูกผิดให้นับ
    if (l.mode === 'lesson') continue
    const key = `${l.cardId}|${l.mode}`
    const s = out.get(key) ?? { seen: 0, wrong: 0, avgMs: 0, lastWrongAt: 0 }
    s.seen++
    const correct = l.rating > 1
    if (!correct) {
      s.wrong++
      s.lastWrongAt = Math.max(s.lastWrongAt, l.ts)
    } else if (l.ms > 0) {
      s.avgMs = s.avgMs === 0 ? l.ms : Math.round(s.avgMs * 0.7 + l.ms * 0.3)
    }
    out.set(key, s)
  }
  return out
}

/**
 * ความอ่อนของใบนี้เฉพาะในโหมดที่กำลังจะถาม
 *
 * null = ยังตอบโหมดนี้ไม่พอจะตัดสิน ให้ใช้คะแนนรวมแทน
 */
export function modeWeakness(
  card: Card,
  stat: ModeStat | undefined,
  refMs: number,
  now = Date.now(),
): Weakness | null {
  if (!stat || stat.seen < MODE_MIN_SEEN) return null

  const base = { cardId: card.id, seen: stat.seen, wrong: stat.wrong }
  const accuracy = (stat.seen - stat.wrong) / stat.seen
  const recent = stat.lastWrongAt > 0 && now - stat.lastWrongAt < RECENT_DAYS * 86_400_000

  if (accuracy < ACCURACY_FLOOR) {
    return {
      ...base,
      score: (1 - accuracy) * 100 + stat.wrong * 5,
      reason: 'wrong',
      accuracy,
      avgMs: stat.avgMs,
    }
  }
  if (recent) return { ...base, score: 40, reason: 'recent', accuracy, avgMs: stat.avgMs }
  if (refMs > 0 && stat.avgMs > refMs * SLOW_FACTOR) {
    return {
      ...base,
      score: Math.min(35, (stat.avgMs / refMs - 1) * 25),
      reason: 'slow',
      accuracy,
      avgMs: stat.avgMs,
    }
  }
  return { ...base, score: 0, reason: 'ok', accuracy, avgMs: stat.avgMs }
}

/** เวลาตอบกลางของโหมดหนึ่ง — เทียบกันเองในโหมดเดียวกัน ไม่ปนข้ามโหมด */
export function medianModeMs(stats: Iterable<ModeStat>): number {
  return median([...stats].map((s) => s.avgMs).filter((n) => n > 0))
}

/** เรียงจากอ่อนสุด · ตัดตัวที่แน่นแล้วกับตัวที่ยังไม่เคยเจอออก */
export function rankWeak(list: Weakness[]): Weakness[] {
  return list.filter((w) => w.score > 0).sort((a, b) => b.score - a.score)
}

export const REASON_LABEL: Record<WeakReason, string> = {
  ok: 'แน่นแล้ว',
  new: 'ยังไม่เจอ',
  wrong: 'ผิดบ่อย',
  recent: 'เพิ่งผิด',
  slow: 'ถูกแต่ช้า',
  lapsed: 'เคยได้แล้วลืม',
  leech: 'ผิดซ้ำ ควรเปลี่ยนวิธีจำ',
}
